import { brand, brandTextColor } from "../../../config/theme";
import type {
  AppSidebarMenuProps,
  SidebarMenuItem,
  SidebarMenuChild,
  CompanyIndicatorProps,
  FooterStatsProps,
  SidebarThemeClasses,
} from "./types";
import { GroupedNav } from "./GroupedNav";

/**
 * Internal sidebar theme classes matching the admin layout's exact design.
 * This ensures every app sidebar looks identical regardless of the layout's own themeClasses.
 */
function getSidebarTheme(isDarkMode: boolean): SidebarThemeClasses {
  return {
    sidebarBg: isDarkMode ? "bg-[#2B2B2B]" : "bg-white",
    border: isDarkMode ? "border-[#4A4A4A]" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-[#3A3A3A]" : "bg-white",
    cardBorder: isDarkMode ? "border-[#4A4A4A]" : "border-gray-200",
    buttonHover: isDarkMode ? "hover:bg-[#3A3A3A]" : "hover:bg-gray-100",
    buttonText: isDarkMode ? "text-gray-400" : "text-gray-600",
    buttonTextHover: isDarkMode ? "hover:text-white" : "hover:text-gray-900",
  };
}

function CompanyIndicator({
  companyId,
  companyName,
  isReseller,
  isDarkMode,
  themeClasses,
}: CompanyIndicatorProps & { themeClasses: SidebarThemeClasses }) {
  return (
    <div
      className={`mb-4 p-3 rounded-xl border ${
        companyId
          ? ""
          : isDarkMode
            ? "bg-[#3A3A3A] border-[#4A4A4A]"
            : "bg-gray-100 border-gray-200"
      }`}
      style={
        companyId
          ? {
              backgroundColor: `${brand.primary}1A`,
              borderColor: `${brand.primary}4D`,
            }
          : undefined
      }
    >
      <div className="flex items-center space-x-2">
        <i
          className={`ri-building-2-line text-lg ${
            companyId ? "" : themeClasses.textSecondary
          }`}
          style={companyId ? { color: brandTextColor } : undefined}
        ></i>
        <div className="flex-1">
          <p
            className={`text-xs font-medium ${
              companyId ? "" : themeClasses.textSecondary
            }`}
            style={companyId ? { color: brandTextColor } : undefined}
          >
            {companyId ? "Compañía seleccionada" : "Sin compañía seleccionada"}
          </p>
          {companyId && companyName ? (
            <p className={`text-sm font-semibold ${themeClasses.text}`}>
              {companyName}
            </p>
          ) : (
            <p className={`text-xs ${themeClasses.textSecondary} mt-0.5`}>
              {isReseller
                ? "Selecciona una compañía en el header"
                : "Contacta a tu administrador"}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function MenuItemButton({
  item,
  isActive,
  isDarkMode,
  themeClasses,
  onNavigate,
  hasChildren,
  expandedMenus,
  onToggleSubmenu,
  isChildActive,
  onCloseMobileMenu,
  isCollapsed,
  onToggleCollapse,
  dense,
}: {
  item: SidebarMenuItem;
  isActive: boolean;
  isDarkMode: boolean;
  themeClasses: SidebarThemeClasses;
  onNavigate: (path: string) => void;
  hasChildren: boolean;
  expandedMenus?: string[];
  onToggleSubmenu?: (menuId: string) => void;
  isChildActive: boolean;
  onCloseMobileMenu: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  dense?: boolean;
}) {
  const isExpanded = expandedMenus?.includes(item.id) ?? false;
  const path = item.path || item.route || "";

  return (
    <div>
      <button
          disabled={item.disabled}
          title={item.disabled ? item.tooltip || "" : ""}
          onClick={() => {
            if (item.disabled) return;
            if (hasChildren && onToggleSubmenu) {
              // Expand the rail first so the revealed submenu has room to show.
              if (isCollapsed) onToggleCollapse?.();
              onToggleSubmenu(item.id);
              return;
            }
            onNavigate(path);
            onCloseMobileMenu();
          }}
          className={`w-full flex items-center justify-between ${isCollapsed ? "lg:justify-center" : ""} ${dense ? "h-9 px-2.5 rounded-lg" : "p-2.5 sm:p-3 rounded-xl"} transition-all
            ${
              item.disabled
                ? "opacity-50 cursor-not-allowed"
                : isActive
                  ? "shadow-lg"
                  : `${themeClasses.buttonText} ${themeClasses.buttonTextHover} ${themeClasses.buttonHover}`
            }
          `}
          style={
            isActive && !item.disabled
              ? { backgroundColor: brand.primary }
              : undefined
          }
        >
          {/* LEFT */}
          <div className={`flex items-center min-w-0 ${dense ? "gap-2.5" : "space-x-3"}`}>
            <i className={`${item.icon} ${dense ? "text-base" : "text-lg"} flex-shrink-0`}></i>
            <span
              className={`font-medium ${dense ? "text-[13px]" : "text-sm sm:text-base"} truncate ${isCollapsed ? "lg:hidden" : ""}`}
            >
              {item.label}
            </span>
          </div>

          {/* RIGHT SIDE */}
          <div
            className={`flex items-center space-x-2 ${isCollapsed ? "lg:hidden" : ""}`}
          >
            {/* Count */}
            {item.loading ? null : item.count !== undefined ? (
              <span
                className={`${dense ? "text-[11px] leading-4 px-1.5 min-w-[20px] text-center tabular-nums" : "text-xs px-2 py-1"} rounded-full ${
                  isActive
                    ? isDarkMode
                      ? "bg-gray-600 text-gray-300"
                      : "bg-gray-100 text-gray-600"
                    : isDarkMode
                      ? "bg-[#4A4A4A] text-gray-300"
                      : "bg-gray-100 text-gray-600"
                }`}
              >
                {item.count}
              </span>
            ) : null}

            {/* Expand/collapse arrow for children */}
            {hasChildren && (
              <i
                className={`ri-arrow-${isExpanded ? "up" : "down"}-s-line text-lg ${
                  isExpanded || isChildActive
                    ? "text-primary-500"
                    : themeClasses.buttonText
                }`}
              ></i>
            )}
          </div>
        </button>

      {/* Sub-menu items */}
      {hasChildren && isExpanded && item.children && (
        <div
          className={`ml-4 mt-1 space-y-1 ${isCollapsed ? "lg:hidden" : ""}`}
        >
          {item.children?.map((child: SidebarMenuChild) => {
            const isChildItemActive =
              typeof window !== "undefined" &&
              child.path &&
              (window.location.pathname === child.path ||
                window.location.pathname.startsWith(child.path + "/"));
            const childHasChildren =
              !!child.children && child.children.length > 0;
            const isChildExpanded =
              expandedMenus?.includes(child.id) ?? false;
            return (
              <div key={child.id}>
                <button
                  onClick={() => {
                    if (child.action) {
                      child.action();
                    } else if (child.path) {
                      onNavigate(child.path);
                      // Reveal nested sub-options (e.g. "Modelos") on navigate.
                      if (childHasChildren && !isChildExpanded) {
                        onToggleSubmenu?.(child.id);
                      }
                    }
                    onCloseMobileMenu();
                  }}
                  className={`w-full flex items-center justify-between p-2 pl-4 rounded-lg transition-all text-left ${
                    isChildItemActive
                      ? "shadow-lg"
                      : `${themeClasses.buttonText} ${themeClasses.buttonTextHover} ${themeClasses.buttonHover}`
                  }`}
                  style={
                    isChildItemActive
                      ? { backgroundColor: brand.primary, color: "white" }
                      : undefined
                  }
                >
                  <div className="flex items-center min-w-0">
                    <i
                      className={`${child.icon} text-sm mr-2 flex-shrink-0`}
                    ></i>
                    <span className="text-sm truncate">{child.label}</span>
                  </div>
                  <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                    {child.loading ? null : child.count !== undefined ? (
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          isChildItemActive
                            ? isDarkMode
                              ? "bg-gray-600 text-gray-300"
                              : "bg-gray-100 text-gray-600"
                            : isDarkMode
                              ? "bg-[#4A4A4A] text-gray-300"
                              : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {child.count}
                      </span>
                    ) : null}
                    {/* Expand/collapse arrow for nested sub-options */}
                    {childHasChildren && (
                      <i
                        role="button"
                        aria-label="toggle submenu"
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggleSubmenu?.(child.id);
                        }}
                        className={`ri-arrow-${isChildExpanded ? "up" : "down"}-s-line text-base cursor-pointer ${
                          isChildItemActive
                            ? ""
                            : isChildExpanded
                              ? "text-primary-500"
                              : themeClasses.buttonText
                        }`}
                      ></i>
                    )}
                  </div>
                </button>

                {/* Nested sub-options (third level, e.g. Dispositivos -> Modelos) */}
                {childHasChildren && isChildExpanded && child.children && (
                  <div className="ml-4 mt-1 space-y-1">
                    {child.children.map((grandChild: SidebarMenuChild) => {
                      const isGrandActive =
                        typeof window !== "undefined" &&
                        grandChild.path &&
                        (window.location.pathname === grandChild.path ||
                          window.location.pathname.startsWith(
                            grandChild.path + "/",
                          ));
                      return (
                        <button
                          key={grandChild.id}
                          onClick={() => {
                            if (grandChild.action) {
                              grandChild.action();
                            } else if (grandChild.path) {
                              onNavigate(grandChild.path);
                            }
                            onCloseMobileMenu();
                          }}
                          className={`w-full flex items-center justify-between p-2 pl-4 rounded-lg transition-all text-left ${
                            isGrandActive
                              ? "shadow-lg"
                              : `${themeClasses.buttonText} ${themeClasses.buttonTextHover} ${themeClasses.buttonHover}`
                          }`}
                          style={
                            isGrandActive
                              ? { backgroundColor: brand.primary, color: "white" }
                              : undefined
                          }
                        >
                          <div className="flex items-center min-w-0 flex-1">
                            <i
                              className={`${grandChild.icon} text-sm mr-2 flex-shrink-0`}
                            ></i>
                            <span className="text-sm leading-tight whitespace-normal break-words text-left">
                              {grandChild.label}
                            </span>
                          </div>
                          {grandChild.loading ? null : grandChild.count !==
                            undefined ? (
                            <span
                              className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ml-2 ${
                                isGrandActive
                                  ? isDarkMode
                                    ? "bg-gray-600 text-gray-300"
                                    : "bg-gray-100 text-gray-600"
                                  : isDarkMode
                                    ? "bg-[#4A4A4A] text-gray-300"
                                    : "bg-gray-100 text-gray-600"
                              }`}
                            >
                              {grandChild.count}
                            </span>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Divider */}
      {item.dividerAfter && (
        <div className={`my-2 border-t ${themeClasses.border}`} />
      )}

      {/* Tooltip */}
      {item.disabled && item.tooltip && (
        <div
          className="
            absolute bottom-full left-1/2 -translate-x-1/2 mb-2
            bg-black text-white text-xs px-2 py-1 rounded
            opacity-0 group-hover:opacity-100
            pointer-events-none transition-all duration-200
            whitespace-nowrap z-50
          "
        >
          {item.tooltip}
        </div>
      )}
    </div>
  );
}

function FooterStats({
  footerStats,
  themeClasses,
  dense,
}: {
  footerStats: FooterStatsProps;
  themeClasses: SidebarThemeClasses;
  dense?: boolean;
}) {
  // Denso: una sola fila por dato, sin título, para que el menú quepa sin
  // scroll; la tarjeta completa se comía ~110 px al pie.
  if (dense) {
    return (
      <div className={`mt-2 pt-2 border-t ${themeClasses.border} space-y-1 px-2.5`}>
        {footerStats.items.map((stat, index) => (
          <div key={index} className="flex justify-between text-[11px]">
            <span className={themeClasses.textSecondary}>{stat.label}</span>
            <span className={`font-semibold tabular-nums ${stat.className || themeClasses.text}`}>
              {stat.value}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div
      className={`mt-8 p-4 ${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl`}
    >
      <h3 className={`text-sm font-semibold ${themeClasses.text} mb-3`}>
        {footerStats.label}
      </h3>
      <div className="space-y-2">
        {footerStats.items.map((stat, index) => (
          <div key={index} className="flex justify-between">
            <span className={`text-xs ${themeClasses.textSecondary}`}>
              {stat.label}
            </span>
            <span
              className={`text-xs font-semibold ${stat.className || themeClasses.text}`}
            >
              {stat.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AppSidebarMenu({
  menuItems,
  isDarkMode,
  isMobileMenuOpen,
  onCloseMobileMenu,
  onNavigate,
  isActiveRoute,
  companyIndicator,
  companySelector,
  footerStats,
  expandedMenus,
  onToggleSubmenu,
  isCollapsed,
  onToggleCollapse,
  grouped,
  dense,
}: AppSidebarMenuProps) {
  const themeClasses = getSidebarTheme(isDarkMode);
  return (
    <>
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onCloseMobileMenu}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          ${themeClasses.sidebarBg} border-r ${themeClasses.border} shadow-sm
          fixed lg:relative inset-y-0 left-0 z-50
          ${dense ? "w-64" : "w-72"} ${isCollapsed ? "lg:w-20" : dense ? "lg:w-56" : "lg:w-72"} h-full min-h-0 transform transition-all duration-300 ease-in-out
          ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
          lg:block overflow-y-auto
        `}
      >
        <div className={`${dense ? "p-2.5" : "p-4 sm:p-6"} ${isCollapsed ? "lg:px-3" : ""}`}>
          {/* Company Selector — solo en móvil; en desktop vive en el Header */}
          {companySelector && (
            <div className="lg:hidden mb-4">{companySelector}</div>
          )}

          {/* Company Indicator + desktop collapse toggle (toggle sits to the right, after the company container) */}
          <div
            className={`flex items-center gap-2 ${
              isCollapsed ? "justify-center mb-3" : ""
            }`}
          >
            {companyIndicator && (
              <div className={`flex-1 min-w-0 ${isCollapsed ? "lg:hidden" : ""}`}>
                <CompanyIndicator
                  {...companyIndicator}
                  themeClasses={themeClasses}
                />
              </div>
            )}

            {onToggleCollapse && (
              <div className="hidden lg:block shrink-0">
                <button
                  onClick={onToggleCollapse}
                  title={isCollapsed ? "Expandir menú" : "Contraer menú"}
                  className={`p-2 ${themeClasses.buttonHover} rounded-lg transition-colors`}
                >
                  <i
                    className={`${isCollapsed ? "ri-menu-unfold-line" : "ri-menu-fold-line"} text-primary-500`}
                  ></i>
                </button>
              </div>
            )}
          </div>

          {/* Menu Items */}
          {grouped ? (
            <nav>
              <GroupedNav
                items={menuItems}
                options={grouped}
                isDarkMode={isDarkMode}
                themeClasses={themeClasses}
                isActiveRoute={isActiveRoute}
                onNavigate={onNavigate}
                onCloseMobileMenu={onCloseMobileMenu}
              />
            </nav>
          ) : (
          <nav className={dense ? "space-y-0.5" : "space-y-1"}>
            {menuItems.map((item) => {
              const path = item.path || item.route || item.id;
              const isActive = isActiveRoute(item.id, path);
              const hasChildren =
                !!item.children && item.children.length > 0;
              const isChildActive =
                hasChildren &&
                !!item.children?.some((child) =>
                  isActiveRoute(child.id, child.path),
                );

              return (
                <div key={item.id} className="relative group">
                  <MenuItemButton
                    item={item}
                    isActive={isActive}
                    isDarkMode={isDarkMode}
                    themeClasses={themeClasses}
                    onNavigate={onNavigate}
                    hasChildren={hasChildren}
                    expandedMenus={expandedMenus}
                    onToggleSubmenu={onToggleSubmenu}
                    isChildActive={isChildActive}
                    onCloseMobileMenu={onCloseMobileMenu}
                    isCollapsed={isCollapsed}
                    onToggleCollapse={onToggleCollapse}
                    dense={dense}
                  />
                </div>
              );
            })}
          </nav>
          )}

          {/* Footer Stats */}
          {footerStats && (
            <div className={isCollapsed ? "lg:hidden" : ""}>
              <FooterStats
                footerStats={footerStats}
                themeClasses={themeClasses}
                dense={dense}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
}
